import React from "react";
import { LayoutDashboard, Crosshair, Trophy, Youtube, Settings, ChevronRight, LogOut, Tag, Webhook, ShieldCheck, Sparkles, Link2, BarChart3, Bookmark } from "lucide-react";
import { BillingBadge } from "./Pricing";

const NAV_ITEMS = [
  { id: "home", label: "Dashboard", icon: LayoutDashboard },
  { id: "dashboard", label: "Analytics", icon: BarChart3 },
  { id: "offers", label: "Offers", icon: Tag },
  { id: "tracking", label: "Tracking", icon: Crosshair },
  { id: "utm", label: "UTM builder", icon: Link2 },
  { id: "integrations", label: "Integrations", icon: Webhook },
  { id: "ask-ai", label: "Ask AI", icon: Sparkles },
  { id: "competitors", label: "Competitors", icon: Trophy },
  { id: "swipe-file", label: "Swipe file", icon: Bookmark },
  { id: "channels", label: "Channels", icon: Youtube },
];

const ADMIN_NAV_ITEM = { id: "admin", label: "Admin", icon: ShieldCheck };

export function Sidebar({ active, onChange, connectedChannel, user, onSignOut, billing, onUpgrade, onManageBilling }) {
  // Admin nav item is conditionally appended for users with is_admin=true
  // (computed server-side from ADMIN_EMAILS env var).
  const items = user?.is_admin ? [...NAV_ITEMS, ADMIN_NAV_ITEM] : NAV_ITEMS;
  return (
    <aside
      className="fixed left-0 top-0 h-screen w-64 border-r border-white/5 bg-black/60 backdrop-blur-xl z-40 flex flex-col"
      data-testid="sidebar"
    >
      {/* Logo lockup — compact so the nav + footer fit on short laptops */}
      <div className="px-6 py-4 flex items-center gap-3 border-b border-white/5">
        <div className="relative">
          <div className="w-9 h-9 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center shadow-[0_0_24px_rgba(255,0,0,0.25)]">
            <Youtube className="w-5 h-5 text-red-500" />
          </div>
        </div>
        <div className="leading-tight">
          <div className="text-white font-semibold tracking-tight">Vid<span className="text-red-500 drop-shadow-[0_0_10px_rgba(255,0,0,0.5)]">ROI</span></div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-medium">Analytics</div>
        </div>
      </div>

      {/* Nav — scrollable when content overflows so the user/channel footer
          (sign-out, connected channel) is always reachable without zooming. */}
      <nav className="px-3 py-3 flex-1 flex flex-col gap-0.5 overflow-y-auto min-h-0">
        <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-600 font-semibold">Menu</div>
        {items.map(({ id, label, icon: Icon }) => {
          const isActive = active === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onChange(id)}
              data-testid={`sidebar-nav-${id}`}
              className={`group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 outline-none
                ${isActive
                  ? "bg-red-500/10 text-red-400 border border-red-500/25 shadow-[0_0_20px_-8px_rgba(255,0,0,0.5)]"
                  : "text-zinc-400 hover:bg-white/[0.04] hover:text-white border border-transparent"
                }`}
            >
              <Icon className={`w-[17px] h-[17px] ${isActive ? "text-red-400" : "text-zinc-500 group-hover:text-zinc-200"}`} />
              <span className="flex-1 text-left">{label}</span>
              {isActive && <ChevronRight className="w-3.5 h-3.5 text-red-400/80" />}
            </button>
          );
        })}
      </nav>

      {/* Footer — pinned at bottom via `mt-auto` + parent flex-col so sign-out
          and channel info never get pushed off-screen, even with 10+ nav items. */}
      <div className="flex-shrink-0 border-t border-white/5">
        <BillingBadge billing={billing} onUpgrade={onUpgrade} onManage={onManageBilling} />

        {connectedChannel && (
          <button
            type="button"
            onClick={() => onChange("channels")}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.04] transition-colors text-left"
            data-testid="sidebar-channel-preview"
          >
            <img
              src={connectedChannel.thumbnail_url}
              alt={connectedChannel.channel_name}
              className="w-9 h-9 rounded-full object-cover border border-white/10"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white font-medium truncate">{connectedChannel.channel_name}</div>
              <div className="text-[11px] text-zinc-500">{formatSubs(connectedChannel.subscriber_count)} subs</div>
            </div>
          </button>
        )}

        {user && (
          <div className="flex items-center gap-3 px-4 py-3 border-t border-white/5" data-testid="sidebar-user">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name || user.email}
                className="w-8 h-8 rounded-full object-cover border border-white/10"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 flex items-center justify-center text-xs font-semibold">
                {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-white font-medium truncate" data-testid="sidebar-user-name">
                {user.name || user.email}
              </div>
              {user.name && <div className="text-[11px] text-zinc-500 truncate">{user.email}</div>}
            </div>
            <button
              type="button"
              onClick={onSignOut}
              className="text-zinc-500 hover:text-red-400 p-1.5 rounded-md hover:bg-red-500/10 transition-colors"
              title="Sign out"
              data-testid="sidebar-signout-btn"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        )}

        {!connectedChannel && !user && (
          <div className="flex items-center gap-3 px-4 py-3 text-zinc-500 text-xs">
            <Settings className="w-4 h-4" />
            <span>No channel connected</span>
          </div>
        )}
      </div>
    </aside>
  );
}

function formatSubs(n) {
  if (!n) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
  return String(n);
}
